import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { PageViewport } from '../../components/PageLayout'
import { getTopbarHeight, useDimensions } from '../../hooks/useDimensions'
import { AppBarComponent } from '../AppBar'
import { FooterBarComponent } from '../FooterBar'
import { AdminCancelListTableComponent } from './Cancel'
import { AdminCommandBar, AdminWorkflowKey } from './AdminCommandBar'
import { AdminIndividualTableComponent } from './Individual'
import { AdminSubscriptionsTableComponent } from './Subscriptions'
import { AuditReportModalComponent, AuditReportData } from './AuditReportModal'
import {
  ADMIN_ALL_UNITS_ROUTE,
  ADMIN_CANCELLATIONS_ROUTE,
  ADMIN_SUBSCRIPTIONS_ROUTE,
  API_PATH_REPORT_AUDIT,
  API_PATH_REPORT_BATCH_SETTLEMENT,
} from '../../config'
import {
  getAuthHeaders,
  getAuthToken,
  getLocalServerURL,
} from '../../utils/getApiConfig'
import { onError } from '../../utils/onError'
import { onSuccess } from '../../utils/onSuccess'
import {
  getUnitsCancel,
  getUnitsIndividual,
  getUnitsMaster,
} from '../Units/selectors'
import { PortalMultiFactorModal } from '../Portal/MultiFactorModal'
import {
  CustomerWorkbenchModal,
  CustomerWorkbenchTab,
} from './CustomerWorkbench/CustomerWorkbenchModal'
import { CreateCustomerRentalWorkflowModal } from './CreateCustomerRentalWorkflowModal'

type AdminViewKey = 'subscriptions' | 'individual' | 'cancel'
type AdminViewSlug = 'subscriptions' | 'allunits' | 'cancellations'
type AdminReportKey = 'audit' | 'batchSettlement'

const VIEW_OPTIONS: Array<{
  key: AdminViewKey
  label: string
  route: string
  slug: AdminViewSlug
}> = [
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    route: ADMIN_SUBSCRIPTIONS_ROUTE,
    slug: 'subscriptions',
  },
  {
    key: 'individual',
    label: 'All Units',
    route: ADMIN_ALL_UNITS_ROUTE,
    slug: 'allunits',
  },
  {
    key: 'cancel',
    label: 'Cancellations',
    route: ADMIN_CANCELLATIONS_ROUTE,
    slug: 'cancellations',
  },
]

const DEFAULT_ADMIN_VIEW: AdminViewKey = 'subscriptions'
const ADMIN_VIEW_BY_SLUG: Record<AdminViewSlug, AdminViewKey> = {
  subscriptions: 'subscriptions',
  allunits: 'individual',
  cancellations: 'cancel',
}
const ADMIN_ROUTE_BY_VIEW: Record<AdminViewKey, string> = {
  subscriptions: ADMIN_SUBSCRIPTIONS_ROUTE,
  individual: ADMIN_ALL_UNITS_ROUTE,
  cancel: ADMIN_CANCELLATIONS_ROUTE,
}

const isAdminViewSlug = (value?: string): value is AdminViewSlug =>
  value === 'subscriptions' || value === 'allunits' || value === 'cancellations'

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10)

const getDefaultBatchSettleRange = () => {
  const now = new Date()
  const from = new Date(now)
  from.setDate(from.getDate() - 30)

  return {
    from: toDateInputValue(from),
    to: toDateInputValue(now),
  }
}

const toBatchSettlementTimestamp = (
  value: string,
  boundary: 'start' | 'end'
) => {
  const suffix =
    boundary === 'start' ? 'T00:00:00.000Z' : 'T23:59:59.999Z'
  return `${value}${suffix}`
}

const triggerDownload = (downloadUrl: string, filename?: string) => {
  const anchor = document.createElement('a')
  anchor.href = downloadUrl
  if (filename) {
    anchor.download = filename
  }
  anchor.rel = 'noopener'
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  window.requestAnimationFrame(() => {
    anchor.click()
  })
  window.setTimeout(() => {
    document.body.removeChild(anchor)
  }, 1000)
}

const compactToolbarButtonSx = {
  height: 56,
  minHeight: 56,
  px: 2,
  py: 0,
  alignSelf: 'stretch',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  boxSizing: 'border-box',
}

export const AdminComponent: React.FC = () => {
  const [batchSettleDialogOpen, setBatchSettleDialogOpen] = React.useState(false)
  const [batchSettleRange, setBatchSettleRange] = React.useState(
    getDefaultBatchSettleRange
  )
  const [auditModalOpen, setAuditModalOpen] = React.useState(false)
  const [multiFactorModalOpen, setMultiFactorModalOpen] = React.useState(false)
  const [customerWorkbenchState, setCustomerWorkbenchState] = React.useState<{
    customerName: string
    customerProfileId: number
  } | null>(null)
  const [selectedCustomerContext, setSelectedCustomerContext] = React.useState<{
    customerName: string
    customerProfileId: number
  } | null>(null)
  const [customerWorkbenchInitialTab, setCustomerWorkbenchInitialTab] =
    React.useState<CustomerWorkbenchTab>('profile')
  const [workflowMode, setWorkflowMode] = React.useState<
    'customerRental' | 'profileOnly' | null
  >(null)
  const [seedCustomers, setSeedCustomers] = React.useState<
    Array<{
      customerProfileId: number
      customerName: string
      email: string
      phoneNumber: string
      portalAccessState?: string
    }>
  >([])
  const [auditReport, setAuditReport] = React.useState<AuditReportData | null>(
    null
  )
  const [reportLoading, setReportLoading] =
    React.useState<AdminReportKey | null>(null)
  const navigate = useNavigate()
  const { adminViewSlug } = useParams<{ adminViewSlug?: string }>()
  const { height, width } = useDimensions()
  const topbarHeight = getTopbarHeight(width)
  const masterUnits = useSelector(getUnitsMaster)
  const individualUnits = useSelector(getUnitsIndividual)
  const cancelUnits = useSelector(getUnitsCancel)
  const selectedView = React.useMemo<AdminViewKey>(() => {
    if (!isAdminViewSlug(adminViewSlug)) return DEFAULT_ADMIN_VIEW
    return ADMIN_VIEW_BY_SLUG[adminViewSlug]
  }, [adminViewSlug])

  const adminSummary = React.useMemo(() => {
    const occupiedUnits = individualUnits.filter(
      (unit) => unit.customerProfileId != null
    ).length
    const activeSubscriptions = individualUnits.filter(
      (unit) => unit.customerProfileId != null && unit.subscriptionId != null
    ).length
    const totalSlots = masterUnits.reduce(
      (accumulator, unit) => accumulator + (unit.quantity || 0),
      0
    )

    return {
      totalSlots,
      occupiedUnits,
      openUnits: Math.max(totalSlots - occupiedUnits, 0),
      activeSubscriptions,
      cancellations: cancelUnits.length,
    }
  }, [cancelUnits.length, individualUnits, masterUnits])

  const adminContentTopOffset = React.useMemo(
    () => Math.max(topbarHeight - 10, 0),
    [topbarHeight]
  )
  const tableMinHeight = 420
  const isBatchSettleRangeInvalid =
    batchSettleRange.from.trim() === '' ||
    batchSettleRange.to.trim() === '' ||
    batchSettleRange.from > batchSettleRange.to

  const handleViewAuditReport = React.useCallback(async () => {
    setReportLoading('audit')
    setAuditReport(null)
    try {
      const headers = await getAuthHeaders()
      if (headers == null) {
        onError('Could not authorize')
        return
      }
      const response = await fetch(getLocalServerURL(API_PATH_REPORT_AUDIT), {
        method: 'GET',
        headers,
      })
      if (!response.ok) {
        onError('Failed to load audit report')
        return
      }
      const data = await response.json()
      if (data?.markdown != null) {
        setAuditReport({
          markdown: data.markdown,
          generatedAt: data.generatedAt ?? '',
          format: data.format ?? 'markdown',
        })
        setAuditModalOpen(true)
      } else {
        onError('Invalid audit report response')
      }
    } catch {
      onError('Failed to load audit report')
    } finally {
      setReportLoading(null)
    }
  }, [])

  const handleDownloadBatchSettlementReport = React.useCallback(async () => {
    setReportLoading('batchSettlement')
    try {
      const authToken = await getAuthToken()
      if (authToken == null) {
        onError('Could not authorize')
        return
      }

      const downloadUrl = new URL(
        getLocalServerURL(API_PATH_REPORT_BATCH_SETTLEMENT)
      )
      downloadUrl.searchParams.set('authToken', authToken)
      downloadUrl.searchParams.set(
        'from',
        toBatchSettlementTimestamp(batchSettleRange.from, 'start')
      )
      downloadUrl.searchParams.set(
        'to',
        toBatchSettlementTimestamp(batchSettleRange.to, 'end')
      )
      triggerDownload(
        downloadUrl.toString(),
        `batch-settlement-report-${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`
      )
      setBatchSettleDialogOpen(false)
    } catch {
      onError('Failed to download batch settle report')
    } finally {
      setReportLoading(null)
    }
  }, [batchSettleRange.from, batchSettleRange.to])

  const handleCloseAuditModal = React.useCallback(() => {
    setAuditModalOpen(false)
  }, [])

  const handleOpenBatchSettleDialog = React.useCallback(() => {
    setBatchSettleDialogOpen(true)
  }, [])

  const handleCloseBatchSettleDialog = React.useCallback(() => {
    setBatchSettleDialogOpen(false)
  }, [])

  const handleBatchSettleRangeChange = React.useCallback(
    (field: 'from' | 'to') =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setBatchSettleRange((current) => ({
          ...current,
          [field]: value,
        }))
      },
    []
  )

  const handleOpenMultiFactorModal = React.useCallback(() => {
    setMultiFactorModalOpen(true)
  }, [])

  const handleCloseMultiFactorModal = React.useCallback(() => {
    setMultiFactorModalOpen(false)
  }, [])

  const handleCloseCustomerWorkbench = React.useCallback(() => {
    setCustomerWorkbenchState(null)
  }, [])

  const handleOpenCustomerWorkbench = React.useCallback(
    (
      customer: { customerProfileId: number; customerName: string },
      initialTab: CustomerWorkbenchTab = 'profile'
    ) => {
      setSelectedCustomerContext(customer)
      setCustomerWorkbenchInitialTab(initialTab)
      setCustomerWorkbenchState(customer)
    },
    []
  )

  const handleOpenWorkflow = React.useCallback((workflow: AdminWorkflowKey) => {
    switch (workflow) {
      case 'createCustomerRental':
        setWorkflowMode('customerRental')
        return
      case 'createCustomerProfile':
        setWorkflowMode('profileOnly')
        return
      case 'createTransaction':
        if (selectedCustomerContext != null) {
          handleOpenCustomerWorkbench(selectedCustomerContext, 'transactions')
          return
        }
        break
      case 'createSubscription':
      case 'cancelSubscription':
        if (selectedCustomerContext != null) {
          handleOpenCustomerWorkbench(selectedCustomerContext, 'subscriptions')
          return
        }
        break
      default:
        break
    }

    onError('Select a customer first to unlock existing-customer workflows.')
  }, [handleOpenCustomerWorkbench, selectedCustomerContext])

  const handleClearSelectedCustomerContext = React.useCallback(() => {
    setSelectedCustomerContext(null)
  }, [])

  const handleReopenSelectedCustomerWorkbench = React.useCallback(() => {
    if (selectedCustomerContext == null) {
      return
    }
    handleOpenCustomerWorkbench(selectedCustomerContext, 'profile')
  }, [handleOpenCustomerWorkbench, selectedCustomerContext])

  const handleWorkflowCompleted = React.useCallback(
    ({
      claimCode,
      customerName,
      customerProfileId,
    }: {
      customerProfileId: number
      customerName: string
      claimCode: string
    }) => {
      const completedCustomer = {
        customerName,
        customerProfileId,
      }

      setWorkflowMode(null)
      setSelectedCustomerContext(completedCustomer)
      setCustomerWorkbenchInitialTab('profile')
      setCustomerWorkbenchState(completedCustomer)
      if (claimCode.trim() !== '') {
        onSuccess(`Customer created. Claim code: ${claimCode}`)
      }
    },
    []
  )

  const selectedCustomerLabel = React.useMemo(() => {
    if (selectedCustomerContext == null) {
      return 'No customer selected'
    }

    return `${selectedCustomerContext.customerName} · #${selectedCustomerContext.customerProfileId}`
  }, [selectedCustomerContext])

  const handleViewChange = React.useCallback(
    (_event: React.MouseEvent<HTMLElement>, value: AdminViewKey | null) => {
      if (value != null) {
        navigate(ADMIN_ROUTE_BY_VIEW[value])
      }
    },
    [navigate]
  )

  React.useEffect(() => {
    if (isAdminViewSlug(adminViewSlug)) return
    navigate(ADMIN_ROUTE_BY_VIEW[DEFAULT_ADMIN_VIEW], { replace: true })
  }, [adminViewSlug, navigate])

  const renderSelectedView = React.useCallback(() => {
    switch (selectedView) {
      case 'cancel':
        return (
          <AdminCancelListTableComponent
            tableHeight="100%"
            tableMinHeight={tableMinHeight}
          />
        )
      case 'individual':
        return (
          <AdminIndividualTableComponent
            tableHeight="100%"
            minHeight={tableMinHeight}
          />
        )
      case 'subscriptions':
      default:
        return (
          <AdminSubscriptionsTableComponent
            onCustomerOptionsChange={(customers) => setSeedCustomers(customers)}
            tableHeight="100%"
            tableMinHeight={tableMinHeight}
          />
        )
    }
  }, [selectedView, tableMinHeight])

  return (
    <>
      <Helmet>
        <title>Home || JustLockIt</title>
        <meta name="keywords" content="HTML,CSS,JavaScript" />
        <meta
          name="description"
          content="justlockit.com admin page. storage units Schulenburg, Texas"
        />
      </Helmet>
      <AppBarComponent fullWidth />
      <PageViewport
        className="admin-page"
        viewportHeight={height}
        sx={{ overflow: 'hidden', px: { xs: 2, sm: 2, md: 1.5 }, py: 1 }}
      >
        <Box
          className="admin-page__content"
          sx={{
            px: { xs: 0, md: 1.5 },
            py: 1,
            mt: `${adminContentTopOffset}px`,
            height: `calc(100% - ${adminContentTopOffset}px)`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Paper
            className="admin-page__toolbar-panel"
            sx={{ p: { xs: 1.5, md: 1.25 }, mb: 1 }}
          >
            <Stack spacing={1.25}>
              <Stack
                className="admin-page__toolbar-header"
                direction={{ xs: 'column', lg: 'row' }}
                justifyContent="space-between"
                spacing={1.25}
              >
                <AdminCommandBar
                  seedCustomers={seedCustomers}
                  selectedCustomer={selectedCustomerContext}
                  onSelectCustomer={handleOpenCustomerWorkbench}
                  onSelectReport={(report) => {
                    if (report === 'audit') {
                      void handleViewAuditReport()
                      return
                    }
                    handleOpenBatchSettleDialog()
                  }}
                  onSelectView={(route) => navigate(route)}
                  onSelectWorkflow={handleOpenWorkflow}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<ShieldOutlinedIcon />}
                    onClick={handleOpenMultiFactorModal}
                    sx={compactToolbarButtonSx}
                  >
                    Set up two-step verification
                  </Button>
                  <Dialog
                    open={batchSettleDialogOpen}
                    onClose={handleCloseBatchSettleDialog}
                    maxWidth="xs"
                    fullWidth
                  >
                    <DialogTitle>Batch settle report range</DialogTitle>
                    <DialogContent>
                      <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                          label="From"
                          type="date"
                          value={batchSettleRange.from}
                          onChange={handleBatchSettleRangeChange('from')}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="To"
                          type="date"
                          value={batchSettleRange.to}
                          onChange={handleBatchSettleRangeChange('to')}
                          InputLabelProps={{ shrink: true }}
                        />
                        {isBatchSettleRangeInvalid ? (
                          <Typography variant="body2" color="error">
                            Enter a valid date range where the start date is on
                            or before the end date.
                          </Typography>
                        ) : null}
                      </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                      <Button onClick={handleCloseBatchSettleDialog}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          void handleDownloadBatchSettlementReport()
                        }}
                        disabled={
                          reportLoading === 'batchSettlement' ||
                          isBatchSettleRangeInvalid
                        }
                      >
                        {reportLoading === 'batchSettlement'
                          ? 'Preparing report…'
                          : 'Download report'}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Stack>
              </Stack>
              {selectedCustomerContext != null ? (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  sx={{
                    px: 0.25,
                    py: 0.5,
                    borderRadius: 1.5,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    backgroundColor: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Selected customer: {selectedCustomerLabel}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleReopenSelectedCustomerWorkbench}
                  >
                    Open Workbench
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleClearSelectedCustomerContext}
                  >
                    Clear
                  </Button>
                </Stack>
              ) : null}
              <Stack
                className="admin-page__toolbar-controls"
                direction={{ xs: 'column', xl: 'row' }}
                justifyContent="space-between"
                spacing={1.25}
              >
                <ToggleButtonGroup
                  className="admin-page__view-toggle"
                  exclusive
                  value={selectedView}
                  onChange={handleViewChange}
                  size="small"
                  color="primary"
                  sx={{ flexWrap: 'wrap' }}
                >
                  {VIEW_OPTIONS.map((option) => (
                    <ToggleButton key={option.key} value={option.key}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Stack
                  className="admin-page__summary-chips"
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                >
                  <Chip
                    label={`${adminSummary.activeSubscriptions} active subscriptions`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`${adminSummary.occupiedUnits} occupied`}
                    size="small"
                  />
                  <Chip
                    label={`${adminSummary.openUnits} open units`}
                    size="small"
                  />
                  <Chip
                    label={`${adminSummary.cancellations} cancellations pending`}
                    size="small"
                  />
                </Stack>
              </Stack>
            </Stack>
          </Paper>
          <Box
            className="admin-page__table-region"
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              '& > *': {
                height: '100%',
                minWidth: 0,
              },
            }}
          >
            {renderSelectedView()}
          </Box>
        </Box>
      </PageViewport>
      <FooterBarComponent fullWidth />
      <AuditReportModalComponent
        open={auditModalOpen}
        onClose={handleCloseAuditModal}
        report={auditReport}
      />
      {multiFactorModalOpen && (
        <PortalMultiFactorModal onClose={handleCloseMultiFactorModal} />
      )}
      {customerWorkbenchState != null ? (
        <CustomerWorkbenchModal
          open
          onClose={handleCloseCustomerWorkbench}
          customerName={customerWorkbenchState.customerName}
          customerProfileId={customerWorkbenchState.customerProfileId}
          initialTab={customerWorkbenchInitialTab}
        />
      ) : null}
        <CreateCustomerRentalWorkflowModal
          mode={workflowMode ?? 'customerRental'}
          open={workflowMode != null}
          onClose={() => setWorkflowMode(null)}
          onCompleted={handleWorkflowCompleted}
        />
    </>
  )
}
